import { useEffect, useState } from "react";
import axios from "axios";
import "./DeveloperPortal.css";

const BASE = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BASE}/api`;

const CURL_EXAMPLE = `curl -X POST "${BASE}/api/oauth/token" \\
  -H "Content-Type: application/x-www-form-urlencoded" \\
  -d "grant_type=client_credentials" \\
  -d "client_id=<YOUR_CLIENT_ID>" \\
  -d "client_secret=gv_<YOUR_SECRET>" \\
  -d "scope=read:referrals"`;

const CURL_USE = `curl -H "Authorization: Bearer <ACCESS_TOKEN>" \\
     "${BASE}/api/v1/governance/compliance/score"`;

const NODE_SIGNING = `import crypto from "crypto";
import express from "express";

const app = express();
const SECRET = process.env.HARBORWELL_WEBHOOK_SECRET;

app.post("/hooks/harborwell",
  express.raw({ type: "application/json" }),
  (req, res) => {
    const ts = req.header("X-HarborWell-Timestamp");
    const sig = req.header("X-HarborWell-Signature");
    const mac = crypto.createHmac("sha256", SECRET)
      .update(ts + "." + req.body)
      .digest("hex");
    if (\`sha256=\${mac}\` !== sig) return res.status(401).end();
    const event = JSON.parse(req.body.toString("utf8"));
    console.log(event.event_type, event.resource_id, event.payload);
    res.status(200).json({ ok: true });
  });`;

const PYTHON_SIGNING = `import hmac, hashlib
from fastapi import FastAPI, Header, HTTPException, Request

app = FastAPI()
SECRET = os.environ["HARBORWELL_WEBHOOK_SECRET"]

@app.post("/hooks/harborwell")
async def hook(request: Request,
               x_harborwell_signature: str = Header(...),
               x_harborwell_timestamp: str = Header(...)):
    raw = await request.body()
    mac = hmac.new(SECRET.encode(),
                   (x_harborwell_timestamp + ".").encode() + raw,
                   hashlib.sha256).hexdigest()
    if f"sha256={mac}" != x_harborwell_signature:
        raise HTTPException(401, "Bad signature")
    event = await request.json()
    print(event["event_type"], event["resource_id"], event["payload"])
    return {"ok": True}`;

function Tab({ id, active, onClick, children, testId }) {
  return (
    <button className={`hw-dev__tab ${active === id ? "hw-dev__tab--active" : ""}`}
            onClick={() => onClick(id)} data-testid={testId}>
      {children}
    </button>
  );
}

export default function DeveloperPortal() {
  const [active, setActive] = useState("start");
  const [overview, setOverview] = useState(null);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    axios.get(`${API}/developer/overview`).then((r) => setOverview(r.data)).catch(() => {});
    axios.get(`${API}/developer/events`).then((r) => setEvents(r.data.events || [])).catch(() => {});
  }, []);

  return (
    <div className="hw-dev" data-testid="developer-portal">
      <div className="hw-dev__inner">
        <div className="hw-dev__eyebrow">HarborWell Connect · Developer Portal</div>
        <h1 className="hw-dev__title">Partner API</h1>
        <p className="hw-dev__lede">
          Build secure integrations with HarborWell Connect. This portal covers
          authentication, rate limits, the event catalog, webhook signing, and
          reference SDK snippets. Interactive OpenAPI is at{" "}
          <a href="/docs" className="hw-dev__chip" style={{ textDecoration: "none" }} data-testid="dev-link-swagger">/docs</a>
          {" and "}
          <a href="/redoc" className="hw-dev__chip" style={{ textDecoration: "none" }} data-testid="dev-link-redoc">/redoc</a>.
        </p>

        <div className="hw-dev__tabs">
          <Tab id="start" active={active} onClick={setActive} testId="dev-tab-start">Getting Started</Tab>
          <Tab id="auth" active={active} onClick={setActive} testId="dev-tab-auth">Authentication</Tab>
          <Tab id="webhooks" active={active} onClick={setActive} testId="dev-tab-webhooks">Webhooks</Tab>
          <Tab id="events" active={active} onClick={setActive} testId="dev-tab-events">Event Catalog</Tab>
          <Tab id="ratelimit" active={active} onClick={setActive} testId="dev-tab-ratelimit">Rate Limits</Tab>
          <Tab id="versioning" active={active} onClick={setActive} testId="dev-tab-versioning">Versioning</Tab>
          <Tab id="changelog" active={active} onClick={setActive} testId="dev-tab-changelog">Changelog</Tab>
        </div>

        {active === "start" && (
          <section className="hw-dev__section" data-testid="dev-section-start">
            <h2>Getting Started</h2>
            <p>
              To integrate with HarborWell Connect, contact
              <strong> ops@harborwell.care</strong> to have your organization
              onboarded. You will receive a Partner ID, a Client ID, and a
              Client Secret (a single-use <code>gv_...</code> token). Store the
              Client Secret securely — HarborWell does not retain a copy.
            </p>
            <ol style={{ color: "#CBD5E1" }}>
              <li>Exchange your Client Credentials for a Bearer token at <code>/api/oauth/token</code>.</li>
              <li>Call scoped endpoints with <code>Authorization: Bearer &lt;token&gt;</code>.</li>
              <li>Subscribe to webhook events via HarborWell staff; you&apos;ll receive HMAC-signed POSTs.</li>
              <li>All responses include <code>X-Request-ID</code>. Include this ID in any support request.</li>
            </ol>
            <h3>Quick example</h3>
            <div className="hw-dev__code" data-testid="dev-code-quickstart">{CURL_EXAMPLE}</div>
            <div className="hw-dev__code" style={{ marginTop: "0.6rem" }}>{CURL_USE}</div>
          </section>
        )}

        {active === "auth" && (
          <section className="hw-dev__section" data-testid="dev-section-auth">
            <h2>Authentication</h2>
            <p>
              Partner integrations use OAuth 2.0 Client Credentials for
              server-to-server calls. API Keys are also supported for
              read-only governance / BI feeds.
            </p>
            {overview && (
              <>
                <div className="hw-dev__card">
                  {overview.authentication.modes.map((m, i) => (
                    <div key={i} style={{ marginBottom: i < overview.authentication.modes.length - 1 ? "1rem" : 0 }}>
                      <h3 style={{ margin: "0 0 0.4rem" }}>{m.name}</h3>
                      {Object.entries(m).filter(([k]) => k !== "name").map(([k, v]) => (
                        <div className="hw-dev__kv" key={k}>
                          <strong>{k}</strong><span>{String(v)}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
                <h3>Available scopes</h3>
                <div>{overview.scopes.map((s) => <span key={s} className="hw-dev__chip">{s}</span>)}</div>
              </>
            )}
            <h3>Token endpoint (curl)</h3>
            <div className="hw-dev__code">{CURL_EXAMPLE}</div>
          </section>
        )}

        {active === "webhooks" && (
          <section className="hw-dev__section" data-testid="dev-section-webhooks">
            <h2>Webhooks</h2>
            <p>
              HarborWell Connect POSTs signed JSON to your registered endpoint
              whenever a subscribed event occurs. Every request carries the
              headers below.
            </p>
            {overview?.webhooks && (
              <div className="hw-dev__card">
                {Object.entries(overview.webhooks).map(([k, v]) => (
                  <div className="hw-dev__kv" key={k}>
                    <strong>{k}</strong><span>{Array.isArray(v) ? v.join(", ") : String(v)}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="hw-dev__note" style={{ margin: "0.5rem 0 1rem" }}>
              Always verify the <code>X-HarborWell-Signature</code> header
              before trusting a payload. Compute
              <code> HMAC-SHA256(secret, timestamp + &quot;.&quot; + raw_body) </code>
              and compare against the value in the header
              (<code>sha256=&lt;hex&gt;</code>). Reject if it doesn&rsquo;t match.
            </div>
            <h3>Node.js example</h3>
            <div className="hw-dev__code">{NODE_SIGNING}</div>
            <h3>Python (FastAPI) example</h3>
            <div className="hw-dev__code">{PYTHON_SIGNING}</div>
          </section>
        )}

        {active === "events" && (
          <section className="hw-dev__section" data-testid="dev-section-events">
            <h2>Event Catalog</h2>
            <p>
              {events.length} events are available for partner subscription.
              Each event has a stable <code>event_type</code> string.
            </p>
            <div className="hw-dev__card" style={{ padding: 0 }}>
              {events.map((e) => (
                <div className="hw-dev__event-row" key={e.event_type} data-testid={`dev-event-${e.event_type}`}>
                  <code>{e.event_type}</code>
                  <div>{e.description}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {active === "ratelimit" && (
          <section className="hw-dev__section" data-testid="dev-section-ratelimit">
            <h2>Rate Limits</h2>
            {overview?.rate_limits && (
              <div className="hw-dev__card">
                {Object.entries(overview.rate_limits).map(([k, v]) => (
                  <div className="hw-dev__kv" key={k}>
                    <strong>{k}</strong><span>{Array.isArray(v) ? v.join(", ") : String(v)}</span>
                  </div>
                ))}
              </div>
            )}
            <p>
              When a limit is exceeded HarborWell responds with HTTP 429 and a
              <code>Retry-After</code> header indicating seconds until the window resets.
              Every 2xx and 4xx response also carries <code>X-RateLimit-Remaining</code>.
            </p>
          </section>
        )}

        {active === "versioning" && (
          <section className="hw-dev__section" data-testid="dev-section-versioning">
            <h2>API Versioning & Lifecycle</h2>
            <p>
              HarborWell Connect follows semantic versioning. Non-breaking
              additions ship immediately. Breaking changes are introduced under
              a new URL prefix (<code>/api/v2/*</code>) with a minimum <strong>180-day</strong>
              deprecation window. Deprecated endpoints emit a <code>Sunset</code>
              header and a link to migration guidance.
            </p>
            <div className="hw-dev__note">
              See the complete lifecycle policy at{" "}
              <code>/app/memory/API_LIFECYCLE_VERSIONING_POLICY.md</code> —
              contact HarborWell for the partner-facing PDF version.
            </div>
          </section>
        )}

        {active === "changelog" && (
          <section className="hw-dev__section" data-testid="dev-section-changelog">
            <h2>Changelog</h2>
            <div className="hw-dev__card">
              <h3 style={{ margin: "0 0 0.35rem" }}>v1.0 (Jul 3, 2026)</h3>
              <ul>
                <li>Introduced Partner Organizations, per-partner API keys, and OAuth 2.0 Client Credentials grant.</li>
                <li>Shipped the Enterprise Webhook Framework with 15 event types, HMAC signing, and reliable delivery with retries + DLQ.</li>
                <li>Added app-wide rate limiting, security-headers middleware, structured logging, and request-ID propagation.</li>
                <li>Published this Developer Portal + machine-readable overview at <code>/api/developer/overview</code>.</li>
              </ul>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
